import { useMemo, useState } from 'react'
import { CalendarClock, Plus } from 'lucide-react'
import { Page } from '@/components/Page'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { useCreatePaySchedule, usePaySchedules } from '@/features/payroll/queries'
import { formatDate } from '@/lib/locale'
import type { PaySchedule } from '@/features/payroll/api'

export function PaySchedulesSettingsPage() {
  const { hasPermission } = useAuth()
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({
    name: '',
    frequency: 'biweekly',
    first_pay_date: '',
    anchor_period_end: '',
    pay_day_offset_days: '0',
    timezone: 'America/New_York',
    currency: 'USD',
    bank_cutoff_hour: '14',
  })

  const { data, isLoading } = usePaySchedules()
  const createMut = useCreatePaySchedule()
  const canManage = hasPermission('payroll.settings.manage')

  const columns = useMemo(
    () => [
      { accessorKey: 'name', header: 'Name' },
      {
        accessorKey: 'frequency',
        header: 'Frequency',
        cell: ({ row }: { row: { original: PaySchedule } }) => (
          <Badge variant="outline">{row.original.frequency.replace(/_/g, ' ')}</Badge>
        ),
      },
      {
        accessorKey: 'first_pay_date',
        header: 'First pay date',
        cell: ({ row }: { row: { original: PaySchedule } }) => formatDate(row.original.first_pay_date),
      },
      { accessorKey: 'timezone', header: 'Timezone' },
      { accessorKey: 'currency', header: 'Currency' },
      {
        accessorKey: 'bank_cutoff_hour',
        header: 'Cutoff',
        cell: ({ row }: { row: { original: PaySchedule } }) => `${row.original.bank_cutoff_hour}:00`,
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        cell: ({ row }: { row: { original: PaySchedule } }) => (
          <Badge variant={row.original.is_active ? 'success' : 'secondary'}>
            {row.original.is_active ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
    ],
    [],
  )

  async function onSave() {
    if (!form.name || !form.first_pay_date || !form.anchor_period_end) {
      toast({
        title: 'Required fields missing',
        description: 'Name, first pay date, and anchor period end are required.',
        variant: 'destructive',
      })
      return
    }
    try {
      await createMut.mutateAsync({
        name: form.name,
        frequency: form.frequency,
        first_pay_date: form.first_pay_date,
        anchor_period_end: form.anchor_period_end,
        pay_day_offset_days: Number(form.pay_day_offset_days) || 0,
        timezone: form.timezone,
        currency: form.currency,
        bank_cutoff_hour: Number(form.bank_cutoff_hour) || 14,
      })
      toast({ title: 'Pay schedule created' })
      setOpen(false)
      setForm({
        name: '',
        frequency: 'biweekly',
        first_pay_date: '',
        anchor_period_end: '',
        pay_day_offset_days: '0',
        timezone: 'America/New_York',
        currency: 'USD',
        bank_cutoff_hour: '14',
      })
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      })
    }
  }

  return (
    <Page
      title="Payroll Settings"
      description="Pay schedules drive run timing. Bank cutoff hour controls when same-day ACH transmission stops accepting batches."
      actions={
        canManage ? (
          <Button onClick={() => setOpen(true)}>
            <Plus className="mr-2 size-4" /> New schedule
          </Button>
        ) : undefined
      }
    >
      <DataTable
        columns={columns as never}
        rows={data?.data ?? []}
        loading={isLoading}
        empty={
          <EmptyState
            icon={CalendarClock}
            title="No pay schedules yet"
            description="Add at least one pay schedule (weekly / biweekly / semimonthly / monthly) so the scheduler can kick off the right pay runs at the right time."
            action={
              canManage ? (
                <Button onClick={() => setOpen(true)}>
                  <Plus className="mr-2 size-4" /> New schedule
                </Button>
              ) : undefined
            }
          />
        }
      />
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="New pay schedule"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onSave} disabled={createMut.isPending}>
              {createMut.isPending ? 'Saving…' : 'Save schedule'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField
            label="Name"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              label="Frequency"
              control={
                <select
                  value={form.frequency}
                  onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly (every 2 weeks)</option>
                  <option value="semimonthly">Semi-monthly (twice a month)</option>
                  <option value="monthly">Monthly</option>
                  <option value="daily">Daily</option>
                  <option value="on_demand">On demand</option>
                </select>
              }
            />
            <FormField
              label="Pay-day offset (days)"
              hint="Days after period end to actually pay. 0 = pay on period-end date."
              inputMode="numeric"
              value={form.pay_day_offset_days}
              onChange={(e) => setForm({ ...form, pay_day_offset_days: e.target.value.replace(/[^\d-]/g, '') })}
            />
            <FormField
              label="First pay date"
              required
              type="date"
              value={form.first_pay_date}
              onChange={(e) => setForm({ ...form, first_pay_date: e.target.value })}
            />
            <FormField
              label="Anchor period end"
              required
              type="date"
              hint="The period-end date that matches your first pay date."
              value={form.anchor_period_end}
              onChange={(e) => setForm({ ...form, anchor_period_end: e.target.value })}
            />
            <FormField
              label="Timezone"
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            />
            <FormField
              label="Bank cutoff hour (24h)"
              inputMode="numeric"
              value={form.bank_cutoff_hour}
              onChange={(e) => setForm({ ...form, bank_cutoff_hour: e.target.value.replace(/\D/g, '') })}
            />
          </div>
        </div>
      </Modal>
    </Page>
  )
}
